import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  format,
  addDays,
  addWeeks,
  addMonths,
  subDays,
  subWeeks,
  subMonths,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
} from 'date-fns';
import CalendarHeader from '../components/calendar/CalendarHeader';
import CalendarView from '../components/calendar/CalendarView';
import BestTimesSidebar from '../components/calendar/BestTimesSidebar';
import ConflictsAlert from '../components/calendar/ConflictsAlert';
import SchedulePostModal from '../components/calendar/SchedulePostModal';
import { useCalendarData, useUnschedulePost } from '../hooks/useCalendar';
import type { CalendarView as CalendarViewType, ScheduledPostEntry, BestTimeSlot, ConflictEntry } from '../types/calendar';

/**
 * CalendarPage component
 *
 * Main calendar page integrating all calendar features:
 * - Calendar grid with day/week/month views
 * - Navigation controls and view toggling
 * - Best time suggestions sidebar
 * - Scheduling conflict alerts
 * - Schedule/reschedule post modal
 */
export default function CalendarPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();

  // View and date state
  const [view, setView] = useState<CalendarViewType>('week');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // Modal state
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleModalMode, setScheduleModalMode] = useState<'schedule' | 'reschedule'>('schedule');
  const [selectedPostForReschedule, setSelectedPostForReschedule] = useState<ScheduledPostEntry | undefined>();
  const [initialDateForSchedule, setInitialDateForSchedule] = useState<Date | undefined>();

  // Timezone (use browser timezone by default)
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Unschedule mutation
  const { mutate: unschedulePost, isPending: isUnscheduling } = useUnschedulePost();

  /**
   * Calculate date range based on view and selected date
   */
  const dateRange = useMemo(() => {
    let start: Date;
    let end: Date;

    switch (view) {
      case 'day':
        start = selectedDate;
        end = selectedDate;
        break;
      case 'week':
        start = startOfWeek(selectedDate, { weekStartsOn: 1 });
        end = endOfWeek(selectedDate, { weekStartsOn: 1 });
        break;
      case 'month':
        // Include days from prev/next month to fill the grid
        start = startOfWeek(startOfMonth(selectedDate), { weekStartsOn: 1 });
        end = endOfWeek(endOfMonth(selectedDate), { weekStartsOn: 1 });
        break;
      default:
        start = startOfWeek(selectedDate, { weekStartsOn: 1 });
        end = endOfWeek(selectedDate, { weekStartsOn: 1 });
    }

    return {
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd'),
    };
  }, [view, selectedDate]);

  // Fetch calendar data
  const {
    calendarView,
    isCalendarLoading,
    isCalendarError,
    calendarError,
    refetchCalendar,
    suggestions,
  } = useCalendarData({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    view,
    timezone,
  });

  // Posts data for the calendar view
  const postsData = useMemo(() => {
    return calendarView?.dates || {};
  }, [calendarView]);

  /**
   * Handle navigation (prev/next/today)
   */
  const handleNavigate = useCallback((direction: 'prev' | 'next' | 'today') => {
    if (direction === 'today') {
      setSelectedDate(new Date());
      return;
    }

    const isPrev = direction === 'prev';

    switch (view) {
      case 'day':
        setSelectedDate((prev) => (isPrev ? subDays(prev, 1) : addDays(prev, 1)));
        break;
      case 'week':
        setSelectedDate((prev) => (isPrev ? subWeeks(prev, 1) : addWeeks(prev, 1)));
        break;
      case 'month':
        setSelectedDate((prev) => (isPrev ? subMonths(prev, 1) : addMonths(prev, 1)));
        break;
    }
  }, [view]);

  /**
   * Handle view change
   */
  const handleViewChange = useCallback((newView: CalendarViewType) => {
    setView(newView);
  }, []);

  /**
   * Handle date change (from header date picker if implemented)
   */
  const handleDateChange = useCallback((date: Date) => {
    setSelectedDate(date);
  }, []);

  /**
   * Handle post click - navigate to post details
   */
  const handlePostClick = useCallback((post: ScheduledPostEntry) => {
    if (workspaceSlug) {
      navigate(`/${workspaceSlug}/posts/${post.postId}`);
    }
  }, [navigate, workspaceSlug]);

  /**
   * Handle add post - open schedule modal
   */
  const handleAddPost = useCallback((date: Date) => {
    setScheduleModalMode('schedule');
    setSelectedPostForReschedule(undefined);
    setInitialDateForSchedule(date);
    setScheduleModalOpen(true);
  }, []);

  /**
   * Handle reschedule post - open schedule modal in reschedule mode
   */
  const handleReschedulePost = useCallback((post: ScheduledPostEntry) => {
    setScheduleModalMode('reschedule');
    setSelectedPostForReschedule(post);
    setInitialDateForSchedule(new Date(post.scheduledAt));
    setScheduleModalOpen(true);
  }, []);

  /**
   * Handle unschedule post
   */
  const handleUnschedulePost = useCallback((post: ScheduledPostEntry) => {
    unschedulePost(post.postId, {
      onSuccess: () => {
        toast.success(t('calendar.unschedule.success'));
        refetchCalendar();
      },
      onError: (error: Error) => {
        toast.error(t('calendar.unschedule.error', { message: error.message }));
      },
    });
  }, [unschedulePost, t, refetchCalendar]);

  /**
   * Handle schedule modal success
   */
  const handleScheduleSuccess = useCallback(() => {
    setScheduleModalOpen(false);
    refetchCalendar();
  }, [refetchCalendar]);

  /**
   * Handle best time suggestion selection
   */
  const handleBestTimeSelect = useCallback((slot: BestTimeSlot) => {
    // Calculate the next occurrence of this day/time
    const now = new Date();
    const targetDay = slot.dayOfWeek;
    const currentDay = now.getDay();

    let daysUntilTarget = targetDay - currentDay;
    if (daysUntilTarget <= 0) {
      daysUntilTarget += 7; // Move to next week
    }

    const targetDate = addDays(now, daysUntilTarget);
    targetDate.setHours(slot.hourOfDay, 0, 0, 0);

    setScheduleModalMode('schedule');
    setSelectedPostForReschedule(undefined);
    setInitialDateForSchedule(targetDate);
    setScheduleModalOpen(true);
  }, []);

  /**
   * Handle conflict click - could navigate to conflicting posts or show detail
   */
  const handleConflictClick = useCallback((conflict: ConflictEntry) => {
    // For now, show the first conflicting post
    if (conflict.posts.length > 0 && workspaceSlug) {
      navigate(`/${workspaceSlug}/posts/${conflict.posts[0].id}`);
    }
  }, [navigate, workspaceSlug]);

  // Error state
  if (isCalendarError && calendarError) {
    return (
      <div className="h-full flex flex-col p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold dark:text-gray-100">
            {t('calendar.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('calendar.subtitle')}
          </p>
        </div>

        {/* Error message */}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="text-6xl mb-4">
              <svg
                className="w-16 h-16 mx-auto text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {t('calendar.errors.loadFailed')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {calendarError.message}
            </p>
            <button
              onClick={() => refetchCalendar()}
              className="px-6 py-3 bg-black dark:bg-white text-white dark:text-black font-bold rounded-lg border-2 border-black dark:border-white hover:shadow-brutal transition-shadow"
            >
              {t('common.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-6">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold dark:text-gray-100">
              {t('calendar.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t('calendar.subtitle')}
            </p>
          </div>

          {/* Schedule button */}
          <button
            onClick={() => handleAddPost(new Date())}
            className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black font-bold rounded-lg border-2 border-black dark:border-white hover:shadow-brutal transition-shadow flex items-center gap-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            {t('calendar.actions.schedulePost')}
          </button>
        </div>

        {/* Calendar Header - Navigation and View Toggle */}
        <CalendarHeader
          view={view}
          selectedDate={selectedDate}
          onViewChange={handleViewChange}
          onDateChange={handleDateChange}
          onNavigate={handleNavigate}
        />
      </div>

      {/* Main Content - Calendar + Sidebar */}
      <div className="flex-1 flex gap-6 min-h-0">
        {/* Calendar View */}
        <div className="flex-1 flex flex-col min-w-0">
          <CalendarView
            view={view}
            selectedDate={selectedDate}
            posts={postsData}
            isLoading={isCalendarLoading}
            onPostClick={handlePostClick}
            onAddPost={handleAddPost}
            onReschedulePost={handleReschedulePost}
            onUnschedulePost={handleUnschedulePost}
          />
        </div>

        {/* Sidebar */}
        <div className="w-80 flex-shrink-0 flex flex-col gap-4 overflow-y-auto">
          {/* Conflicts Alert */}
          <ConflictsAlert
            queryParams={{
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
              resolved: false,
            }}
            onConflictClick={handleConflictClick}
          />

          {/* Best Times Sidebar */}
          <BestTimesSidebar onTimeSelect={handleBestTimeSelect} />
        </div>
      </div>

      {/* Schedule Post Modal */}
      <SchedulePostModal
        isOpen={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        onSuccess={handleScheduleSuccess}
        mode={scheduleModalMode}
        initialDate={initialDateForSchedule}
        existingPost={selectedPostForReschedule}
        timezone={timezone}
        suggestions={suggestions?.suggestions}
      />

      {/* Unscheduling indicator */}
      {isUnscheduling && (
        <div className="fixed inset-0 bg-black/20 dark:bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-6 shadow-brutal">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black dark:border-white" />
              <p className="font-medium dark:text-gray-100">
                {t('calendar.unschedule.loading')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
